import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import type { RootState } from '../../../../store';
import ParamsEditor from './ParamsEditor';
import { typeLabelOf } from '../../nodeCatalog';
import SqlEditorModal from './SqlEditorModal';
import { useTranslation } from 'react-i18next';
import ShellCommandSection from './ShellCommandSection';
import { needsCheck, OPEN_SQL_CHECK_EVENT, type NodeSqlCheck } from '../../../../services/sqlCheck';
import { formatShortDateTime } from '../../../../utils/formatters';

interface Props { node: any }

/*
 * 아래 두 조각은 **패널 밖**에 둔다. 패널 함수 안에서 만들면 글자를 하나 칠 때마다 새 컴포넌트가 되어
 * React 가 안에 든 입력칸을 통째로 다시 붙인다 — SQL 인라인 편집기에서 글자를 지우면 커서가 사라지던 이유다.
 */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-3 rounded border border-gray-200 p-2"><div className="text-xs font-semibold text-gray-600 mb-1">{title}</div>{children}</div>
);

const Check: React.FC<{ checked: boolean; label: string; onChange: (v: boolean) => void }> = ({ checked, label, onChange }) => (
  <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />{label}
  </label>
);

/** 마지막 검증 요약 + 「검증」「검증 기록」 — 요약은 노드에 저장된다(cfg.sqlCheck), SQL 을 고치면 「바뀐 뒤 검증 안 함」 */
const SqlCheckStatus: React.FC<{ cfg: any; onCheck: () => void; onLog: () => void }> = ({ cfg, onCheck, onLog }) => {
  const { t } = useTranslation();
  const last: NodeSqlCheck | undefined = cfg.sqlCheck;
  if (!(cfg.sql || '').trim()) return null;
  const changed = needsCheck(cfg);
  const text = changed
    ? (last ? t('property:sqlCheck.node.changed') : t('property:sqlCheck.node.never'))
    : t(`property:sqlCheck.node.${last?.status || 'never'}`, { at: formatShortDateTime(last?.at), error: last?.error || 0, warn: last?.warn || 0 });
  const tone = changed ? 'text-gray-500' : last?.status === 'ok' ? 'text-green-700' : last?.status === 'error' || last?.status === 'failed' ? 'text-red-700' : last?.status === 'warn' ? 'text-amber-700' : 'text-gray-500';
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <span className={`flex-1 min-w-0 truncate text-[0.6875rem] ${tone}`} title={text}>{text}</span>
      <button type="button" disabled={!cfg.connection} onClick={onCheck} title={cfg.connection ? t('property:sqlCheck.buttonTitle') : t('property:sqlCheck.noConnection')}
        className="px-2 py-0.5 text-xs rounded border border-blue-600 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50">{t('property:sqlCheck.node.check')}</button>
      <button type="button" onClick={onLog} className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700">{t('property:sqlCheck.node.log')}</button>
    </div>
  );
};

/**
 * 실행 분류 노드 속성 — 실행(runWorkflow) / 외부명령어(shellCommand) / SQL실행(sqlScript).
 * 공통: Label, 변수/파라미터 표. 본문(명령·SQL·값)의 ${이름} 은 실행 시 치환된다(실행 conf > 노드 파라미터 > 전역변수).
 */
const ExecNodePanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const workflows = useSelector((s: any) => s.project?.workflows || []);
  const currentWf = useSelector((s: any) => s.workflow?.current?.id);
  const currentProjectId = useSelector((s: any) => s.project?.currentProjectId || s.project?.selectedProjectId);
  const connections = useSelector((s: RootState) => s.connections.list);
  // 워크플로 설정에서 식별 주석을 끄면 노드에서 켜도 붙지 않는다
  const wfSqlComment = useSelector((s: any) => s.workflow?.settings?.sqlComment !== false);
  const cfg = node?.data?.config || {};
  const type = String(node.type);
  // SQL 편집기 — 열 때 오른쪽에 무엇을 보일지(검증 결과·기록)와 열자마자 검증할지
  const [sqlEditor, setSqlEditor] = useState<{ pane?: 'templates' | 'results' | 'log'; auto?: boolean } | null>(null);
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.nodeId === node.id) setSqlEditor({ pane: d.pane || 'results' });
    };
    window.addEventListener(OPEN_SQL_CHECK_EVENT, h);
    return () => window.removeEventListener(OPEN_SQL_CHECK_EVENT, h);
  }, [node.id]);

  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  const wfOptions = workflows.filter((w: any) => w.id !== currentWf && (!currentProjectId || w.projectId === currentProjectId));
  const check = (k: string, def = false) => ({ checked: cfg[k] ?? def, onChange: (v: boolean) => patch({ [k]: v }) });

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500">{t('property:exec.nodeType')}</span>
          <input type="text" value={typeLabelOf(type)} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} />
        </div>
      </div>
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} />
      </div>

      {type === 'runWorkflow' && (
        <Section title={t('property:exec.runWorkflow')}>
          <select className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white mb-1.5" value={cfg.workflowId || ''} onChange={(e) => patch({ workflowId: e.target.value })}>
            <option value="">{t('property:exec.choose')}</option>
            {wfOptions.map((w: any) => <option key={w.id} value={w.id}>{w.icon ? `${w.icon} ` : ''}{w.name} ({w.id})</option>)}
          </select>
          <div className="flex justify-end mb-1.5">
            <button type="button" className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-50"
              disabled={!cfg.workflowId} title={t('property:exec.previewTitle')}
              onClick={() => window.dispatchEvent(new CustomEvent('openflow:preview-workflow', { detail: { workflowId: cfg.workflowId } }))}>
              {t('property:exec.preview')}
            </button>
          </div>
          <div className="text-[0.6875rem] text-gray-500 mb-1.5">{t('property:exec.runWorkflowHint')} <code>{'${name}'}</code> {t('property:exec.runWorkflowHintTail')}</div>
          <div className="flex items-center gap-4">
            <Check {...check('wait', true)} label={t('property:exec.wait')} />
            <label className="flex items-center gap-1 text-xs text-gray-700">{t('property:exec.timeout')}<input type="number" className="w-20 border border-gray-300 rounded px-1 py-0.5 bg-white" value={cfg.timeoutSecs ?? 3600} onChange={(e) => patch({ timeoutSecs: Number(e.target.value) })} /></label>
          </div>
          {/* 실행 전 대기 — 같이 시작하는 실행 노드를 조금씩 늦춘다(테라스트림 exec_prj_block 의 delay_time) */}
          <div className="flex items-center gap-2 mt-1.5">
            <label className="flex items-center gap-1 text-xs text-gray-700">{t('property:exec.delaySecs')}
              <input type="number" min={0} className="w-20 border border-gray-300 rounded px-1 py-0.5 bg-white" value={cfg.delaySecs ?? 0}
                onChange={(e) => patch({ delaySecs: Math.max(0, Number(e.target.value) || 0) })} />
            </label>
            <span className="text-[0.6875rem] text-gray-500">{t('property:exec.delayHint')}</span>
          </div>
        </Section>
      )}

      {type === 'shellCommand' && (
        <Section title={t('property:exec.shell')}>
          <ShellCommandSection cfg={cfg} patch={patch} />
        </Section>
      )}

      {type === 'sqlScript' && (
        <Section title={t('property:exec.sqlScript')}>
          <select className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white mb-1.5" value={cfg.connection || ''} onChange={(e) => patch({ connection: e.target.value })}>
            <option value="">{t('property:exec.chooseConnection')}</option>
            {connections.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}{c.conn_type ? ` (${c.conn_type})` : ''}</option>)}
          </select>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[0.6875rem] text-gray-500">{t('property:exec.lines', { n: (cfg.sql || '').split(/\r?\n/).length })}</span>
            <button type="button" className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={() => setSqlEditor({})} title={t('property:exec.openEditorTitle')}>{t('property:exec.openEditor')}</button>
          </div>
          {sqlEditor && (
            <SqlEditorModal title={t('property:exec.sqlEditTitle', { name: cfg.label || node.data?.label || 'SQL' })} value={cfg.sql || ''} connection={cfg.connection}
              check={{ workflowId: currentWf, nodeId: node.id, nodeLabel: cfg.label || node.data?.label, params: cfg.params || [], last: cfg.sqlCheck }}
              initialPane={sqlEditor.pane} autoCheck={sqlEditor.auto}
              onSave={(v, chk) => { patch({ sql: v, ...(chk ? { sqlCheck: chk } : {}) }); setSqlEditor(null); }} onClose={() => setSqlEditor(null)} />
          )}
          <textarea className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-white" rows={8} value={cfg.sql || ''} onChange={(e) => patch({ sql: e.target.value })}
            placeholder={t('property:exec.sqlPlaceholder')} />
          <SqlCheckStatus cfg={cfg} onCheck={() => setSqlEditor({ pane: 'results', auto: true })} onLog={() => setSqlEditor({ pane: 'log' })} />
          <div className="text-[0.6875rem] text-gray-500 mt-1 mb-1.5">{t('property:exec.sqlHint')}</div>
          <Check {...check('fetchResult', true)} label={t('property:exec.fetchResult')} />
          <div title={t('property:exec.sqlCommentHint')}>
            <Check checked={wfSqlComment && cfg.sqlComment !== false} onChange={(v) => patch({ sqlComment: v })} label={t('property:exec.sqlComment')} />
            {!wfSqlComment && <div className="ml-5 text-[0.6875rem] text-gray-500">{t('property:exec.sqlCommentOffByWorkflow')}</div>}
          </div>
          <Check {...check('captureDbOutput', true)} label={t('property:exec.captureOutput')} />
          {/* 프로시저가 예외를 잡아 출력만 하고 정상 종료하면 노드는 성공으로 보인다 — 출력으로 실패를 가린다 */}
          {(cfg.captureDbOutput ?? true) && (
            <div className="ml-5">
              <Check {...check('failOnDbError', false)} label={t('property:exec.failOnDbError')} />
              {cfg.failOnDbError && (
                <label className="flex items-center gap-1 text-xs text-gray-700 mt-1">
                  {t('property:exec.dbErrorPattern')}
                  <input className="flex-1 border border-gray-300 rounded px-1 py-0.5 bg-white font-mono" value={cfg.dbErrorPattern || ''}
                    placeholder={t('property:exec.dbErrorPatternDefault')} onChange={(e) => patch({ dbErrorPattern: e.target.value })} />
                </label>
              )}
              <div className="text-[0.6875rem] text-gray-500 mt-0.5">{t('property:exec.failOnDbErrorHint')}</div>
            </div>
          )}
          <div className="text-[0.6875rem] text-gray-500 mt-1">
            {t('property:exec.outputHint1')} <b>{t('property:exec.outputHintLog')}</b>{t('property:exec.outputHint2')}
            <br />
            {t('property:exec.plsqlHint1')}<code>BEGIN … END;</code>{t('property:exec.plsqlHint2')} <code>/</code> {t('property:exec.plsqlHint3')}
          </div>
        </Section>
      )}

      <ParamsEditor items={cfg.params || []} onChange={(items) => patch({ params: items })} />
    </div>
  );
};

export default ExecNodePanel;
